import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Button, Field, Alert } from "@evolua/ui";
import { AuthApiError, mfaAssociate } from "../../auth/auth-api.js";
import { useAuth } from "../../auth/AuthContext.js";
import { getAuthMode } from "../../auth/config.js";
import { getChallenge, setChallenge } from "../../auth/token-storage.js";
import { track } from "../../telemetry/product-events.js";
import { AuthLayout } from "./AuthLayout.js";

export function MfaSetupPage() {
  const mode = getAuthMode();
  const auth = useAuth();
  const navigate = useNavigate();
  const initial = useRef(getChallenge());
  const stored = initial.current;

  const [secretCode, setSecretCode] = useState<string | null>(null);
  const [session, setSession] = useState(stored?.session ?? "");
  const [code, setCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [loadingSecret, setLoadingSecret] = useState(false);

  useEffect(() => {
    if (mode !== "api" || !stored || stored.challengeName !== "MFA_SETUP") return;
    let cancelled = false;
    setLoadingSecret(true);
    void mfaAssociate(stored.session)
      .then((res) => {
        if (cancelled) return;
        setSecretCode(res.secretCode);
        setSession(res.session);
        setChallenge({
          email: stored.email,
          session: res.session,
          challengeName: "MFA_SETUP",
        });
        track("mfa.enrollment_started");
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(
          err instanceof AuthApiError
            ? err.detail || "Não foi possível iniciar MFA."
            : "Falha ao associar TOTP.",
        );
      })
      .finally(() => {
        if (!cancelled) setLoadingSecret(false);
      });
    return () => {
      cancelled = true;
    };
  }, [mode, stored]);

  if (mode === "demo") {
    return <Navigate to="/login" replace />;
  }

  if (auth.status === "authenticated" && !recoveryCodes) {
    return <Navigate to="/" replace />;
  }

  if (!stored || stored.challengeName !== "MFA_SETUP") {
    return <Navigate to="/login" replace />;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!stored) return;
    setError(null);
    setPending(true);
    try {
      const codes = await auth.completeMfaSetup({
        email: stored.email,
        session,
        code: code.trim(),
      });
      track("mfa.enrollment_completed");
      setRecoveryCodes(codes);
    } catch (err) {
      setError(
        err instanceof AuthApiError
          ? err.detail || "Código inválido."
          : "Falha ao confirmar MFA.",
      );
    } finally {
      setPending(false);
    }
  }

  if (recoveryCodes) {
    return (
      <AuthLayout
        title="Guarde seus códigos"
        lead="Cada código de recuperação só pode ser usado uma vez. Salve-os offline agora."
      >
        <div className="evolua-auth__codes" aria-label="Códigos de recuperação">
          {recoveryCodes.map((c) => (
            <span key={c}>{c}</span>
          ))}
        </div>
        <Button type="button" onClick={() => navigate("/", { replace: true })}>
          Ir ao portal
        </Button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Configurar MFA"
      lead="Escaneie o segredo no app autenticador (Google Authenticator, Authy, etc.) e confirme o código."
    >
      {loadingSecret ? <p className="evolua-auth__lead">Gerando segredo TOTP…</p> : null}
      {secretCode ? (
        <>
          <p className="evolua-auth__lead">
            No autenticador, escolha “Inserir chave de configuração” e cole o segredo
            abaixo (tipo: baseado em tempo / TOTP).
          </p>
          <p className="evolua-auth__lead">Segredo (Evolua HCM):</p>
          <div className="evolua-auth__secret">{secretCode}</div>
        </>
      ) : null}
      <form className="evolua-auth__form" onSubmit={onSubmit}>
        <Field
          label="Código de 6 dígitos"
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="[0-9]{6}"
          maxLength={6}
          required
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
        />
        {error ? <Alert tone="danger">{error}</Alert> : null}
        <Button
          type="submit"
          loading={pending}
          disabled={!secretCode || code.length !== 6}
        >
          Ativar MFA
        </Button>
      </form>
      <div className="evolua-auth__links">
        <Link to="/login">Voltar ao login</Link>
      </div>
    </AuthLayout>
  );
}
